import type { ReactElement } from 'react';
import { Body1, Subtitle1 } from '@fluentui/react-components';
import {
  CalendarClockRegular,
  CalendarErrorRegular,
  CalendarLtrRegular,
  PersonQuestionMarkRegular,
} from '@fluentui/react-icons';
import { TabbedContent, type TTabConfig } from '@shared/components/TabbedContent';
import { TERMINOLOGY } from '@shared/i18n';
import { TASK_DUE_SOON_DAYS } from '../../tasks/constants';
import type { ETaskStatus, TTaskDashboard, TTaskListItem } from '../../tasks/types';
import AttentionTasksTable from './AttentionTasksTable';

type TNeedsAttention = TTaskDashboard['needsAttention'];
type TProps = {
  projectId: string;
  needsAttention: TNeedsAttention;
  statusPending: boolean;
  onStatusChange: (task: TTaskListItem, status: ETaskStatus) => void;
};

const NeedsAttentionSection = ({ projectId, needsAttention, statusPending, onStatusChange }: TProps) => {
  const tab = (
    id: keyof TNeedsAttention,
    label: string,
    icon: ReactElement,
    disabledTooltip: string
  ): TTabConfig<keyof TNeedsAttention> => ({
    id,
    label,
    icon,
    count: needsAttention[id].length,
    showCount: true,
    disabledTooltip,
    content: (
      <AttentionTasksTable
        projectId={projectId}
        tasks={needsAttention[id]}
        showAssignee={id !== 'unassigned'}
        statusPending={statusPending}
        onStatusChange={onStatusChange}
      />
    ),
  });
  const tasks = TERMINOLOGY.task.lowerPlural;
  const tabs = [
    tab('overdue', 'Overdue', <CalendarErrorRegular />, `No overdue ${tasks}.`),
    tab('dueSoon', 'Due soon', <CalendarClockRegular />, `No ${tasks} due in the next ${TASK_DUE_SOON_DAYS} days.`),
    tab('upcoming', `Upcoming ${tasks}`, <CalendarLtrRegular />, `No ${tasks} due later.`),
    tab('unassigned', 'Unassigned', <PersonQuestionMarkRegular />, `No unassigned ${tasks}.`),
  ];

  return (
    <div className="flex flex-col space-y-4">
      <Subtitle1>Needs attention</Subtitle1>
      {tabs.some((item) => item.count) ? (
        <TabbedContent tabs={tabs} autoSelectFirstNonEmpty />
      ) : (
        <Body1 className="text-neutralFg-3">No {TERMINOLOGY.task.lowerPlural} need attention.</Body1>
      )}
    </div>
  );
};
export default NeedsAttentionSection;
